/**
 * @fileoverview Firebase Shared Sandbox Session Service
 *
 * Manages shared sandbox sessions where two friends can collaboratively
 * place and move pieces on the same board in real time.
 */

import {
  doc,
  setDoc,
  updateDoc,
  onSnapshot,
  Timestamp,
} from 'firebase/firestore';
import { db } from './firebaseConfig';

const SANDBOX_SESSIONS_COLLECTION = 'sandbox_sessions';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface SandboxSession {
  hostUid: string;
  guestUid: string;
  users: string[];           // [uid1, uid2] sorted, for security rules
  fen: string;               // Current board state
  status: 'waiting' | 'active' | 'closed';
  createdAt: Date;
  lastUpdatedBy: string;     // uid of who last changed the board
}

// ─── Service ────────────────────────────────────────────────────────────────

export class SandboxSessionService {
  /**
   * Generate a deterministic session ID from two UIDs (same pattern as friendships).
   */
  static getSessionId(uid1: string, uid2: string): string {
    return uid1 < uid2 ? `${uid1}_${uid2}` : `${uid2}_${uid1}`;
  }

  /**
   * Create a new shared sandbox session.
   * Returns the session ID.
   */
  static async createSession(hostUid: string, guestUid: string): Promise<string> {
    const sessionId = this.getSessionId(hostUid, guestUid);
    const users = [hostUid, guestUid].sort();

    await setDoc(doc(db, SANDBOX_SESSIONS_COLLECTION, sessionId), {
      hostUid,
      guestUid,
      users,
      fen: '8/8/8/8/8',   // Empty 5x8 board
      status: 'waiting',
      createdAt: Timestamp.now(),
      lastUpdatedBy: hostUid,
    });

    return sessionId;
  }

  /**
   * Mark session as active (when guest joins).
   */
  static async joinSession(sessionId: string): Promise<void> {
    await updateDoc(doc(db, SANDBOX_SESSIONS_COLLECTION, sessionId), {
      status: 'active',
    });
  }

  /**
   * Update the board state (FEN) after a piece is placed or moved.
   */
  static async updateBoard(sessionId: string, fen: string, updatedBy: string): Promise<void> {
    await updateDoc(doc(db, SANDBOX_SESSIONS_COLLECTION, sessionId), {
      fen,
      lastUpdatedBy: updatedBy,
    });
  }

  /**
   * Close the sandbox session.
   */
  static async closeSession(sessionId: string): Promise<void> {
    await updateDoc(doc(db, SANDBOX_SESSIONS_COLLECTION, sessionId), {
      status: 'closed',
    });
  }

  /**
   * Subscribe to real-time session changes.
   * Returns an unsubscribe function.
   */
  static subscribeSession(
    sessionId: string,
    callback: (session: SandboxSession | null) => void
  ): () => void {
    return onSnapshot(doc(db, SANDBOX_SESSIONS_COLLECTION, sessionId), (snap) => {
      if (!snap.exists()) {
        callback(null);
        return;
      }
      const data = snap.data();
      callback({
        hostUid: data.hostUid,
        guestUid: data.guestUid,
        users: data.users,
        fen: data.fen,
        status: data.status,
        createdAt: data.createdAt?.toDate?.() || new Date(),
        lastUpdatedBy: data.lastUpdatedBy,
      });
    });
  }
}
